import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMMITTEES } from "@/lib/constants";
import { newsletterTerms, currentSkin } from "@/lib/skin";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Newspaper, Loader2, Save, Send, ShieldCheck, Clock, Info, Copy } from "lucide-react";
import { toast } from "sonner";

const ANNOUNCEMENT_TEMPLATE = `[ANNOUNCEMENT HEADLINE]

[Committee name] invites chapter members to [briefly describe the event, opportunity, deadline, or announcement].

Date: [Month, day, year]
Time: [Time]
Location: [Location or virtual platform]
Deadline: [Deadline, if applicable]

[Provide one short paragraph with the most important details, participation requirements, and any items members should bring or submit.]

Action Required: [Register, RSVP, volunteer, submit, attend, or contact.]
Link: [Insert link]
Contact: [Name and email address]`;

const empty = {
  requestor_first: "", requestor_last: "", email: "", contact_number: "",
  committee: "", title: "", links: "", notes: "",
  headline: "", summary: "", event_date: "", event_time: "", location: "",
  deadline: "", details: "", action_required: "", link: "",
  contact_name: "", contact_email: "",
};

function buildAnnouncement(f) {
  const lines = [];
  if (f.headline) lines.push(f.headline.toUpperCase());
  if (f.summary) lines.push(`\n${f.committee ? f.committee + " " : ""}${f.summary}`);
  const meta = [];
  if (f.event_date) meta.push(`Date: ${f.event_date}`);
  if (f.event_time) meta.push(`Time: ${f.event_time}`);
  if (f.location) meta.push(`Location: ${f.location}`);
  if (f.deadline) meta.push(`Deadline: ${f.deadline}`);
  if (meta.length) lines.push("\n" + meta.join("\n"));
  if (f.details) lines.push(`\n${f.details}`);
  if (f.action_required) lines.push(`\nAction Required: ${f.action_required}`);
  if (f.link) lines.push(`Link: ${f.link}`);
  if (f.contact_name || f.contact_email) lines.push(`Contact: ${[f.contact_name, f.contact_email].filter(Boolean).join(" — ")}`);
  return lines.join("\n");
}

const Field = ({ label, children, required, hint }) => (
  <div>
    <Label className="flex items-center gap-1">{label}{required && <span className="text-accent">*</span>}</Label>
    {hint && <p className="mb-1.5 mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function SundayNewsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const nameParts = (user?.name || "").split(" ");
  const [form, setForm] = useState({
    ...empty,
    requestor_first: nameParts[0] || "",
    requestor_last: nameParts.slice(1).join(" ") || "",
    email: user?.email || "",
    committee: user?.committee || "",
  });
  const [content, setContent] = useState([]); // required: doc/PDF
  const [media, setMedia] = useState([]);      // optional: images/videos
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    if (id) {
      api.get(`/submissions/${id}`).then((r) => {
        setForm({ ...empty, ...r.data.data });
        setContent(r.data.data.documents || []);
        setMedia(r.data.data.images || []);
        setLoaded(true);
      }).catch(() => { toast.error("Could not load submission"); navigate("/submissions"); });
    }
  }, [id, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (status) => {
    if (status === "submitted") {
      const missing = [];
      if (!form.requestor_first || !form.requestor_last) missing.push("requestor name");
      if (!form.email) missing.push("email");
      if (!form.contact_number) missing.push("contact number");
      if (!form.committee) missing.push("committee / initiative");
      if (!form.title) missing.push("project/event title");
      if (!form.headline) missing.push("announcement headline");
      if (!form.details) missing.push("details");
      if (!form.links) missing.push("related links (enter N/A if none)");
      if (missing.length) { toast.error(`Please complete: ${missing.join(", ")}`); return; }
    }
    setSaving(true);
    const data = { ...form, documents: content, images: media, announcement_copy: buildAnnouncement(form) };
    try {
      if (id) await api.put(`/submissions/${id}`, { submission_type: "sunday_news", data, status });
      else await api.post("/submissions", { submission_type: "sunday_news", data, status });
      toast.success(status === "draft" ? "Saved as draft" : `${newsletterTerms().name} submission sent for review!`);
      navigate("/submissions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="sunday-news-form">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Newspaper className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{newsletterTerms().name} Submission</h1>
        </div>
      </div>

      {/* Deadline notice */}
      <div className="flex items-start gap-3 rounded-xl border p-4" data-testid="sn-deadline-notice"
        style={{ backgroundColor: "hsl(345 48% 97%)", borderColor: "hsl(345 48% 82%)" }}>
        <Clock className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "hsl(345 48% 58%)" }} />
        <div className="text-sm">
          <p className="font-semibold text-foreground">Submission deadline — Wednesday at 12:00 PM (Noon)</p>
          <p className="mt-1 text-muted-foreground">
            To ensure timely publication, all submissions must be received by Wednesday at Noon for that week's issue. This deadline will be strictly enforced.
            Late submissions are not guaranteed placement and may run the following week unless time-sensitive or approved by the Basileus.
          </p>
          <p className="mt-1 text-muted-foreground">Questions? Contact the Epistoleus at <a href="mailto:correspondingsecretary@alphaepsilongammaomega.org" className="font-medium underline hover:no-underline" style={{ color: "hsl(345 48% 50%)" }} data-testid="sn-epistoleus-email">correspondingsecretary@alphaepsilongammaomega.org</a>.</p>
        </div>
      </div>

      <Card className="space-y-5 p-6">
        <h2 className="font-heading text-lg font-semibold">Requestor</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="First name" required><Input data-testid="sn-first" value={form.requestor_first} onChange={(e) => set("requestor_first", e.target.value)} /></Field>
          <Field label="Last name" required><Input data-testid="sn-last" value={form.requestor_last} onChange={(e) => set("requestor_last", e.target.value)} /></Field>
          <Field label="Email" required><Input type="email" data-testid="sn-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="example@example.com" /></Field>
          <Field label="Contact number" required><Input data-testid="sn-phone" value={form.contact_number} onChange={(e) => set("contact_number", e.target.value)} placeholder="(000) 000-0000" /></Field>
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        <h2 className="font-heading text-lg font-semibold">Submission details</h2>
        <Field label="Committee / Initiative" required>
          <Select value={form.committee} onValueChange={(v) => set("committee", v)}>
            <SelectTrigger data-testid="sn-committee"><SelectValue placeholder="Select committee or initiative" /></SelectTrigger>
            <SelectContent>{COMMITTEES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Project / Event title" required>
          <Input data-testid="sn-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Founders Day Program" />
        </Field>

        {form.committee && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary" data-testid="sn-routing-note">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <p>This submission will be reviewed by chapter leadership (Basileus or Anti-Basileus) before it is released to Communications.</p>
          </div>
        )}
      </Card>

      <Card className="space-y-5 p-6">
        <div>
          <h2 className="font-heading text-lg font-semibold">Announcement content</h2>
          <p className="text-sm text-muted-foreground">Enter your announcement directly below — no separate document needed. Fill in what applies.</p>
        </div>
        <Field label="Announcement headline" required>
          <Input data-testid="sn-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="e.g. Founders Day Program — All Members Welcome" />
        </Field>
        <Field label="Summary line" hint="Completes: “[Committee] invites chapter members to …”">
          <Input data-testid="sn-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} placeholder="invites chapter members to celebrate Founders Day…" />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Date"><Input data-testid="sn-event-date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} placeholder="Month, day, year" /></Field>
          <Field label="Time"><Input data-testid="sn-event-time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="e.g. 6:00 PM" /></Field>
          <Field label="Location"><Input data-testid="sn-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Location or virtual platform" /></Field>
          <Field label="Deadline"><Input data-testid="sn-deadline" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} placeholder="If applicable" /></Field>
        </div>
        <Field label="Details" required hint="One short paragraph: the most important details, requirements, and anything to bring or submit.">
          <Textarea data-testid="sn-details" rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} />
        </Field>
        <Field label="Action required" hint="Register, RSVP, volunteer, submit, attend, or contact.">
          <Input data-testid="sn-action" value={form.action_required} onChange={(e) => set("action_required", e.target.value)} />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Contact name" hint="May be different from the requestor."><Input data-testid="sn-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></Field>
          <Field label="Contact email"><Input data-testid="sn-contact-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="name@example.org" /></Field>
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        <h2 className="font-heading text-lg font-semibold">Attachments</h2>
        <Field label="Flyer or supporting document" hint="Optional. Attach a DOC or PDF if you have a designed flyer or extra material.">
          <FileUpload testId="sn-content" files={content} onChange={setContent} accept=".doc,.docx,.pdf,.txt" />
        </Field>
        <Field label="Images and videos" hint="Optional. Don't forget to include the names and titles of the individuals in the photos.">
          <FileUpload testId="sn-media" files={media} onChange={setMedia} accept="image/*,video/*" />
        </Field>
        <Field label="Related links & URLs" required hint='If none, enter "N/A". Sharing a Canva or Google file? Set access to "anyone with the link can edit."'>
          <Textarea data-testid="sn-links" rows={2} value={form.links} onChange={(e) => set("links", e.target.value)} placeholder="Paste related links or enter N/A" />
        </Field>
        <Field label="Additional notes for Communications" hint="Optional — anything the team should know.">
          <Textarea data-testid="sn-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
      </Card>

      <div className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>All fields marked with <span className="text-accent">*</span> are required.</p>
      </div>

      <div className="sticky bottom-4 flex gap-3 rounded-xl border border-border bg-card/95 p-3 backdrop-blur">
        <Button variant={currentSkin() === "aka" ? "default" : "outline"} onClick={() => save("draft")} disabled={saving} data-testid="sn-save-draft" className={`flex-1 ${currentSkin() === "aka" ? "bg-green-700 text-white hover:bg-green-800" : ""}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save draft</>}
        </Button>
        <Button onClick={() => save("submitted")} disabled={saving} data-testid="sn-submit" className="flex-1 border-0 text-white hover:opacity-90" style={{ backgroundColor: "hsl(345 48% 70%)" }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Submit</>}
        </Button>
      </div>
    </div>
  );
}
